import { useEffect, useRef, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import { Markdown } from "@/components/Markdown";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Send, Trash2, Sparkles, Hexagon } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Design a turn-based RPG matchmaking service with Elo rating, a Redis leaderboard and a MySQL inventory schema.",
  "Architect a real-time card game backend in Golang using WebSocket, with Redis session state and Kubernetes autoscaling.",
  "Generate a PHP-to-Golang migration plan for a legacy guild system with MySQL performance tuning.",
];

export const AgentChat = () => {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const loadSessions = useCallback(async () => {
    const res = await api.get("/chat/sessions");
    setSessions(res.data);
    return res.data;
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const openSession = async (id) => {
    setActive(id);
    const res = await api.get(`/chat/sessions/${id}/messages`);
    setMessages(res.data);
  };

  const newSession = async () => {
    const res = await api.post("/chat/sessions", { title: "New game system" });
    await loadSessions();
    setActive(res.data.session_id);
    setMessages([]);
    return res.data.session_id;
  };

  const removeSession = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/chat/sessions/${id}`);
    const list = await loadSessions();
    if (active === id) { setActive(null); setMessages([]); }
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    let sid = active;
    if (!sid) sid = await newSession();
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content }]);
    setStreaming("");
    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sid, message: content }),
      });
      if (!resp.ok || !resp.body) throw new Error("stream failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setStreaming(acc);
      }
      setMessages((m) => [...m, { role: "assistant", content: acc }]);
      setStreaming("");
      loadSessions();
    } catch {
      toast.error("Agent request failed.");
      setStreaming("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full">
      <div className="w-60 shrink-0 border-r border-slate-800 bg-slate-950/40 flex flex-col">
        <div className="p-3">
          <Button onClick={newSession} data-testid="new-session-btn"
            className="w-full rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
            <Plus className="w-4 h-4 mr-1" /> New system
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-1">
          {sessions.map((s) => (
            <button key={s.session_id} onClick={() => openSession(s.session_id)} data-testid={`session-${s.session_id}`}
              className={`group w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors flex items-center justify-between gap-2 ${
                active === s.session_id ? "bg-slate-800 text-slate-100" : "text-slate-400 hover:bg-slate-800/50"}`}>
              <span className="truncate">{s.title}</span>
              <Trash2 onClick={(e) => removeSession(s.session_id, e)} data-testid={`delete-session-${s.session_id}`}
                className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 hover:text-red-400 transition-opacity shrink-0" />
            </button>
          ))}
          {sessions.length === 0 && <p className="text-xs text-slate-600 px-3 py-4 font-mono">No systems yet.</p>}
        </div>
      </div>

      <div className="flex-1 flex flex-col min-w-0">
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 lg:px-8 py-6" data-testid="chat-scroll">
          {messages.length === 0 && !streaming ? (
            <div className="max-w-2xl mx-auto mt-10 text-center">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-5">
                <Hexagon className="w-7 h-7 text-emerald-400" />
              </div>
              <h2 className="font-display text-2xl font-bold text-white">Describe your game backend</h2>
              <p className="mt-2 text-slate-400 text-sm">The digital twin will design the architecture and generate Golang/PHP code, schemas and manifests.</p>
              <div className="mt-8 space-y-3 text-left">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => send(s)} data-testid={`suggestion-${i}`}
                    className="w-full text-left glass rounded-xl border border-slate-800 p-4 text-sm text-slate-300 hover:border-emerald-500/50 transition-colors flex gap-3">
                    <Sparkles className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-6">
              {messages.map((m, i) => (
                <div key={`${m.role}-${i}`} data-testid={`message-${m.role}-${i}`} className={m.role === "user" ? "flex justify-end" : ""}>
                  {m.role === "user" ? (
                    <div className="bg-emerald-500/15 border border-emerald-500/25 rounded-2xl rounded-br-sm px-4 py-3 text-slate-100 text-sm max-w-[85%]">
                      {m.content}
                    </div>
                  ) : (
                    <div className="glass border border-slate-800 rounded-2xl rounded-bl-sm px-5 py-4 max-w-full">
                      <Markdown>{m.content}</Markdown>
                    </div>
                  )}
                </div>
              ))}
              {streaming && (
                <div data-testid="message-streaming" className="glass border border-slate-800 rounded-2xl rounded-bl-sm px-5 py-4">
                  <Markdown>{streaming}</Markdown>
                  <span className="inline-block w-2 h-4 bg-emerald-400 animate-pulse-dot ml-0.5 align-middle" />
                </div>
              )}
              {busy && !streaming && (
                <div className="text-sm text-slate-500 font-mono flex items-center gap-2"><Hexagon className="w-4 h-4 text-emerald-400 animate-pulse-dot" /> analyzing requirements…</div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-slate-800 p-4 bg-slate-950/40">
          <div className="max-w-3xl mx-auto flex items-end gap-3">
            <Textarea value={input} onChange={(e) => setInput(e.target.value)} data-testid="chat-input"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Describe your game system requirements…" rows={1}
              className="resize-none bg-slate-900 border-slate-800 text-slate-100 min-h-[48px] max-h-40" />
            <Button onClick={() => send()} disabled={busy || !input.trim()} data-testid="chat-send-btn"
              className="rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 h-12 w-12 p-0 shrink-0">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
